import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["selected", "hoverBg", "theme", "onKeyDown", "outerRef", "disableKb"],
    _excluded2 = ["children", "liProps"],
    _excluded3 = ["theme", "disableKb", "onKeyDown"],
    _excluded4 = ["title", "selected", "theme", "disableKb", "onKeyDown", "children", "collapsible"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * For use within menu popovers
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { space, minWidth, width, display, overflow, color, borders } from 'styled-system';
import Box from '../Box';
import BaseHR from '../HR';
import Button from '../Button';
import Text from '../Text';
import CheckmarkIcon from '../Icons/CheckmarkIcon';
import RightIcon from '../Icons/RightIcon';
import { focusOnFocusable } from '../focus';
import { pipe, useThemeDefaults, truncate, useRandomId } from '../utils';
import { keyUpEvent, keyDownEvent } from '../utils/kb';
import { minTextMargins, marginToPadding } from '../space';
import { textStyle, cursor } from '../theme';
export var Outer = useThemeDefaults(function (props) {
  return {
    as: 'ul',
    role: 'menu',
    pt: "".concat(props.theme.menuList.spacing, "px"),
    pb: "".concat(props.theme.menuList.spacing, "px"),
    overflow: 'auto',
    maxHeight: '85vh'
  };
}, styled(Box).withConfig({
  displayName: "MenuList__Outer",
  componentId: "sc-rmfrs7-0"
})(overflow));
Outer.propTypes = _objectSpread({}, Box.propTypes);
var minTextPaddings = pipe(minTextMargins, marginToPadding);
minTextPaddings.propTypes = minTextMargins.propTypes;
export var ItemOuter = styled(Box).withConfig({
  displayName: "MenuList__ItemOuter",
  componentId: "sc-rmfrs7-1"
})({});
ItemOuter.propTypes = _objectSpread({}, Box.propTypes);

var hoverBg = function hoverBg(props) {
  return {
    ':hover': {
      backgroundColor: props.theme.colors[props.hoverBg] || props.hoverBg
    }
  };
};

hoverBg.propTypes = {
  hoverBg: PropTypes.string
};

var hoverColor = function hoverColor(props) {
  return props.hoverColor ? {
    ':hover': {
      color: props.theme.colors[props.hoverColor] || props.hoverColor
    }
  } : {};
};

hoverColor.propTypes = {
  hoverColor: PropTypes.string
};

var focusStyle = function focusStyle(props) {
  return {
    ':focus': {
      outline: 'none'
    },
    ':focus-visible': {
      outline: 'none',
      boxShadow: "0px 0px 3px ".concat(props.theme.colors.focus)
    }
  };
};

export var ItemInner = styled('button').withConfig({
  displayName: "MenuList__ItemInner",
  componentId: "sc-rmfrs7-2"
})(cursor, color, hoverBg, hoverColor, focusStyle, textStyle, minTextPaddings, space, width, minWidth, display, borders, truncate);
export var ItemText = useThemeDefaults(function (_ref) {
  var _ref$lines = _ref.lines,
      lines = _ref$lines === void 0 ? 1 : _ref$lines;
  return {
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    textStyle: 'bodyRegular'
  };
}, Text);
export var ItemCaption = useThemeDefaults(function () {
  return {
    whiteSpace: 'nowrap',
    ml: '10px',
    color: 'labelCaption',
    textStyle: 'bodyRegular'
  };
}, Text);
export var ItemLayout = useThemeDefaults(function () {
  return {
    display: 'inline-flex',
    width: '100%',
    justifyContent: 'space-between'
  };
}, Box);
ItemInner.propTypes = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, hoverBg.propTypes), textStyle.propTypes), minTextPaddings.propTypes), space.propTypes), width.propTypes), minWidth.propTypes), display.propTypes), color.propTypes), borders.propTypes);
/**
 * Traverse to the nearest sibling
 */

export var getSiblingItem = function getSiblingItem(direction, el) {
  var currentEl = el;

  while (currentEl) {
    currentEl = currentEl["".concat(direction, "Sibling")];

    if (!currentEl || currentEl.getAttribute('role') === 'menuitem') {
      return currentEl;
    }
  }
};
export var focusNextSibling = function focusNextSibling(el) {
  if (el) {
    focusOnFocusable(getSiblingItem('next', el));
  }
};
export var focusPrevSibling = function focusPrevSibling(el) {
  if (el) {
    focusOnFocusable(getSiblingItem('previous', el));
  }
};
export var handleKbInteraction = function handleKbInteraction(e, outerRef) {
  if (keyUpEvent(e)) {
    focusPrevSibling(outerRef.current);
    e.preventDefault();
  }

  if (keyDownEvent(e)) {
    focusNextSibling(outerRef.current);
    e.preventDefault();
  }
};
export var menuListInnerDefaults = function menuListInnerDefaults(_ref2) {
  var selected = _ref2.selected,
      hoverBg = _ref2.hoverBg,
      theme = _ref2.theme,
      _ref2$onKeyDown = _ref2.onKeyDown,
      _onKeyDown = _ref2$onKeyDown === void 0 ? function () {} : _ref2$onKeyDown,
      outerRef = _ref2.outerRef,
      disableKb = _ref2.disableKb,
      props = _objectWithoutProperties(_ref2, _excluded);

  return {
    display: 'block',
    color: props.disabled ? 'labelPlaceholder' : 'label',
    hoverBg: hoverBg || theme.menuList.hoverBg,
    pl: selected === false || selected === true ? theme.menuList.paddingXSelected : theme.menuList.paddingX,
    pr: theme.menuList.paddingX,
    sa: "spacer".concat(theme.menuList.spacing),
    sb: "spacer".concat(theme.menuList.spacing),
    textStyle: theme.menuList.textStyle,
    minWidth: theme.menuList.minWidth,
    width: '100%',
    cursor: props.disabled ? 'default' : 'pointer',
    type: !props.type && props.as === 'button' ? 'button' : undefined,
    tabIndex: selected ? 1 : 0,
    onKeyDown: function onKeyDown(e) {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      _onKeyDown.apply(void 0, [e].concat(args));

      if (!disableKb) {
        handleKbInteraction(e, outerRef);
      }
    }
  };
};

var _ref4 = /*#__PURE__*/React.createElement(CheckmarkIcon, {
  width: "13px",
  height: "13px",
  position: "absolute",
  top: "7px",
  left: "20px"
});

export var Item = withTheme(function (_ref3) {
  var children = _ref3.children,
      liProps = _ref3.liProps,
      props = _objectWithoutProperties(_ref3, _excluded2);

  var outerRef = /*#__PURE__*/React.createRef();
  return /*#__PURE__*/React.createElement(ItemOuter, _extends({
    as: "li",
    role: props.disabled ? undefined : 'menuitem',
    position: "relative",
    ref: outerRef
  }, liProps), /*#__PURE__*/React.createElement(ItemInner, _extends({}, menuListInnerDefaults(_objectSpread({
    outerRef: outerRef
  }, props)), props), /*#__PURE__*/React.createElement(React.Fragment, null, props.selected === true && _ref4, children)));
});
process.env.NODE_ENV !== "production" ? Item.propTypes = _objectSpread({}, ItemInner.propTypes) : void 0;
export var ButtonItem = withTheme(function (_ref5) {
  var theme = _ref5.theme,
      disableKb = _ref5.disableKb,
      _ref5$onKeyDown = _ref5.onKeyDown,
      _onKeyDown2 = _ref5$onKeyDown === void 0 ? function () {} : _ref5$onKeyDown,
      buttonProps = _objectWithoutProperties(_ref5, _excluded3);

  var outerRef = /*#__PURE__*/React.createRef();
  return /*#__PURE__*/React.createElement(Box, {
    as: "li",
    m: "0px",
    role: "menuitem",
    px: theme.menuList.inlineButtonPaddingX,
    ref: outerRef
  }, /*#__PURE__*/React.createElement(Button, _extends({
    m: "0px",
    mb: theme.menuList.inlineButtonMarginBottom,
    primary: true,
    width: theme.menuList.inlineButtonWidth,
    onKeyDown: function onKeyDown(e) {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }

      _onKeyDown2.apply(void 0, [e].concat(args));

      if (!disableKb) {
        handleKbInteraction(e, outerRef);
      }
    }
  }, buttonProps)));
});
process.env.NODE_ENV !== "production" ? ButtonItem.propTypes = _objectSpread({}, Button.propTypes) : void 0;
export var HR = function HR(props) {
  return /*#__PURE__*/React.createElement(BaseHR, _extends({
    sb: "spacer15",
    sa: "spacer15"
  }, props));
};
process.env.NODE_ENV !== "production" ? HR.propTypes = _objectSpread({}, BaseHR.propTypes) : void 0;

var rotate = function rotate(degrees) {
  return {
    transform: "rotate(".concat(degrees, "deg)"),
    transition: 'transform',
    transitionDuration: '250ms'
  };
};

var _ref7 = /*#__PURE__*/React.createElement(RightIcon, {
  width: 10,
  color: "labelPlaceholder"
});

export var ItemGroup = withTheme(function (_ref6) {
  var _ref6$title = _ref6.title,
      title = _ref6$title === void 0 ? null : _ref6$title,
      _ref6$selected = _ref6.selected,
      selected = _ref6$selected === void 0 ? false : _ref6$selected,
      theme = _ref6.theme,
      disableKb = _ref6.disableKb,
      _ref6$onKeyDown = _ref6.onKeyDown,
      onKeyDown = _ref6$onKeyDown === void 0 ? function () {} : _ref6$onKeyDown,
      children = _ref6.children,
      _ref6$collapsible = _ref6.collapsible,
      collapsible = _ref6$collapsible === void 0 ? false : _ref6$collapsible,
      innerProps = _objectWithoutProperties(_ref6, _excluded4);

  var id = useRandomId(innerProps.id);

  var _React$useState = React.useState(!selected),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      collapsed = _React$useState2[0],
      setCollapsed = _React$useState2[1];

  var outerRef = /*#__PURE__*/React.createRef();
  var toggleCollapsed = React.useCallback(function () {
    collapsible && setCollapsed(!collapsed);
  }, [collapsible, collapsed, setCollapsed]);
  var expanded = !collapsible || !collapsed;
  var disabledHover = collapsible ? {} : {
    cursor: 'default',
    hoverBg: 'inherit'
  };
  return /*#__PURE__*/React.createElement(ItemOuter, {
    as: "li",
    role: "menuitem",
    position: "relative",
    ref: outerRef,
    width: ['85vw', '290px']
  }, title && /*#__PURE__*/React.createElement(ItemInner, _extends({}, menuListInnerDefaults(_objectSpread(_objectSpread({
    selected: selected,
    theme: theme,
    disableKb: disableKb,
    onKeyDown: onKeyDown,
    outerRef: outerRef
  }, disabledHover), innerProps)), {
    pl: theme.menuList.paddingX,
    pr: "spacer15",
    py: "spacer10",
    onClick: toggleCollapsed,
    color: "label",
    width: "100%",
    minWidth: "unset",
    "aria-controls": id,
    "aria-expanded": !collapsible || expanded ? "true" : "false"
  }, disabledHover, innerProps), /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    width: "100%",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Text, {
    as: "span",
    my: "0px",
    title: title,
    textStyle: theme.menuList.textStyle,
    truncate: true
  }, title), collapsible && /*#__PURE__*/React.createElement(Box, {
    style: rotate(expanded ? 90 : 0)
  }, _ref7))), /*#__PURE__*/React.createElement(Outer, {
    id: id,
    maxHeight: "unset",
    paddingTop: title ? 0 : 'spacer10',
    paddingBottom: 'spacer10',
    display: expanded ? 'block' : 'none'
  }, children));
});
Outer.ButtonItem = ButtonItem;
Outer.Item = Item;
Outer.ItemText = ItemText;
Outer.ItemCaption = ItemCaption;
Outer.ItemLayout = ItemLayout;
Outer.HR = HR;
Outer.ItemGroup = ItemGroup;
export default Outer;